import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BuyCrypto from '../component/BuyCrypto.js';
import PostMessageBridge from '../utils/postMessageBridge.js';
import { parseWidgetConfig, normalizeTheme, normalizeLanguage } from '../config/widgetConfig.js';

/**
 * WidgetPage - Standalone widget page for iframe embedding
 */
const WidgetPage = () => {
  const [searchParams] = useSearchParams();

  // Parse configuration from URL parameters (single source of truth)
  const config = useMemo(() => parseWidgetConfig(), [searchParams]);

  // Dynamic theme state (allows real-time postMessage switching without iframe reload)
  const [activeTheme, setActiveTheme] = useState(() => normalizeTheme(config.theme));

  // Dynamic language state (allows real-time postMessage switching without iframe reload)
  const [activeLanguage, setActiveLanguage] = useState(() => normalizeLanguage(config.language));

  // Sync when URL params change
  useEffect(() => {
    setActiveTheme(normalizeTheme(config.theme));
    setActiveLanguage(normalizeLanguage(config.language));
  }, [config.theme, config.language]);

  // Initialize postMessage bridge
  const bridge = useMemo(() => {
    const b = new PostMessageBridge(config.parentOrigin);
    b.init();
    return b;
  }, [config.parentOrigin]);

  // Real-time parent postMessage listener for theme and language changes
  useEffect(() => {
    const handleParentMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      const { type, payload } = event.data;

      if (type === 'THEME_CHANGE') {
        const incoming = payload?.theme || event.data.theme;
        if (incoming) {
          setActiveTheme(normalizeTheme(incoming));
        }
      } else if (type === 'LANGUAGE_CHANGE' || type === 'LOCALE_CHANGE') {
        const incoming = payload?.lang || payload?.locale || event.data.lang || event.data.locale;
        if (incoming) {
          setActiveLanguage(normalizeLanguage(incoming));
        }
      } else if (type === 'SYNC_PARENT_STATE') {
        const incomingTheme = payload?.theme;
        const incomingLang = payload?.lang || payload?.locale;
        if (incomingTheme) {
          setActiveTheme(normalizeTheme(incomingTheme));
        }
        if (incomingLang) {
          setActiveLanguage(normalizeLanguage(incomingLang));
        }
      }
    };

    window.addEventListener('message', handleParentMessage);

    if (bridge) {
      bridge.on('THEME_CHANGE', (payload) => {
        const incoming = payload?.theme;
        if (incoming) setActiveTheme(normalizeTheme(incoming));
      });
      bridge.on('LANGUAGE_CHANGE', (payload) => {
        const incoming = payload?.lang || payload?.locale;
        if (incoming) setActiveLanguage(normalizeLanguage(incoming));
      });
      bridge.on('LOCALE_CHANGE', (payload) => {
        const incoming = payload?.lang || payload?.locale;
        if (incoming) setActiveLanguage(normalizeLanguage(incoming));
      });
      bridge.on('SYNC_PARENT_STATE', (payload) => {
        if (payload?.theme) setActiveTheme(normalizeTheme(payload.theme));
        if (payload?.lang || payload?.locale) {
          setActiveLanguage(normalizeLanguage(payload.lang || payload.locale));
        }
      });
    }

    return () => {
      window.removeEventListener('message', handleParentMessage);
      if (bridge) {
        bridge.off('THEME_CHANGE');
        bridge.off('LANGUAGE_CHANGE');
        bridge.off('LOCALE_CHANGE');
        bridge.off('SYNC_PARENT_STATE');
      }
    };
  }, [bridge]);

  // Apply theme configuration
  useEffect(() => {
    const root = document.documentElement;

    const themeCardBgMap = {
      dark: '#0B0F18',
      cyber: '#181820',
      mono: '#1A1A1A',
      light: '#FFFFFF',
    };
    const currentCardBg = themeCardBgMap[activeTheme] || '#0B0F18';

    if (config.primaryColor) {
      root.style.setProperty('--primary-color', config.primaryColor);
      const hoverColor = adjustColorBrightness(config.primaryColor, -20);
      root.style.setProperty('--primary-hover', hoverColor);
    }

    // Always ensure --widget-bg matches active theme card color unless overridden
    root.style.setProperty('--widget-bg', config.backgroundColor || currentCardBg);

    if (config.borderRadius) {
      root.style.setProperty('--border-radius', `${config.borderRadius}px`);
    }

    // Apply theme class to documentElement and body
    root.classList.remove('dark-theme', 'cyber-theme', 'mono-theme', 'light-theme');
    root.classList.add(`${activeTheme}-theme`);

    document.body.classList.remove('dark-theme', 'cyber-theme', 'mono-theme', 'light-theme');
    document.body.classList.add(`${activeTheme}-theme`);

    // Set documentElement and body background
    if (config.isEmbedded) {
      root.style.height = '100%';
      root.style.background = currentCardBg;

      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.height = '100%';
      document.body.style.overflowX = 'hidden';
      document.body.style.overflowY = 'auto';
      document.body.style.background = currentCardBg;
    } else {
      const standaloneBgMap = {
        dark: '#04070F',
        cyber: '#111118',
        mono: '#0F0F0F',
        light: '#FAFCFE',
      };
      const standaloneBg = standaloneBgMap[activeTheme] || '#04070F';
      root.style.background = standaloneBg;
      document.body.style.background = standaloneBg;
    }

    return () => {
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--primary-hover');
      root.style.removeProperty('--widget-bg');
      root.style.removeProperty('--border-radius');
      root.classList.remove('dark-theme', 'cyber-theme', 'mono-theme', 'light-theme');
      document.body.classList.remove('dark-theme', 'cyber-theme', 'mono-theme', 'light-theme');
    };
  }, [config, activeTheme]);

  const activeConfig = useMemo(() => ({
    ...config,
    theme: activeTheme,
    language: activeLanguage,
  }), [config, activeTheme, activeLanguage]);

  return (
    <BuyCrypto
      config={activeConfig}
      bridge={bridge}
      isEmbedded={config.isEmbedded}
      language={activeLanguage}
    />
  );
};

/**
 * Adjust color brightness
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to adjust (-255 to 255)
 * @returns {string} Adjusted hex color
 */
function adjustColorBrightness(hex, amount) {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Handle short hex format
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  // Parse RGB values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default WidgetPage;
